import {hpcAttrs} from '@github-ui/hpc'
import {Commits as CommitsList} from '@github-ui/commits/shared/Commits'
import {CommitsBlankState} from '@github-ui/commits/shared/CommitsBlankState'
import {useAnalytics} from '@github-ui/use-analytics'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useLoadDeferredCommitDataPaginated} from '@github-ui/commits/shared/useLoadDeferredCommitData'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash, SplitPageLayout} from '@primer/react'
import {Navigate} from '@github-ui/react-router'
import {useEffect, useEffectEvent} from 'react'
import {useCommitsLiveUpdates} from '../hooks/use-commits-live-updates'
import type {CommitsRouteWithHeaderDataPayload} from './route-payload-types'
import {pullRequestsCommitsRoute, pullRequestsCommitsWithRangeRoute} from './commits-route'
import {pullRequestsChangesWithRangeRoute} from './files-changed-routes'
import {pullRequestsLayoutRoute} from './layout-route'
import {useIsDockedPanelCompact, useIsDockedPanelOpen} from '../contexts/DockedPanelContext'
import styles from './Commits.module.css'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {clsx} from 'clsx'

const loggingPrefix = 'prx_commits.'

export function CommitsEntrypoint() {
  const {
    data: {pullRequest, aliveChannels, repository},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {
    data: {metadata, commitGroups, timeOutMessage, truncated},
    dataUpdatedAt,
  } = useRouteQuery(pullRequestsCommitsRoute, 'mainQuery')

  return (
    <CommitsComponent
      aliveChannels={aliveChannels}
      aliveChannelThrottleTimeout={10_000}
      commitGroups={commitGroups}
      dataUpdatedAt={dataUpdatedAt}
      metadata={metadata}
      pullRequest={pullRequest}
      repository={repository}
      timeOutMessage={timeOutMessage}
      truncated={truncated}
    />
  )
}

export function CommitsWithRangeEntrypoint() {
  const {owner, repo, pr_number, range} = useRouteParams(pullRequestsCommitsWithRangeRoute)
  const to = pullRequestsChangesWithRangeRoute.generatePath({owner, repo, pr_number, range})
  return <Navigate to={to} replace />
}

function CommitsComponent({
  commitGroups,
  metadata: {deferredCommitsDataUrl, commitHeadShaChannel},
  aliveChannels,
  pullRequest,
  repository,
  timeOutMessage,
  truncated,
  aliveChannelThrottleTimeout,
  dataUpdatedAt,
}: Pick<
  CommitsRouteWithHeaderDataPayload,
  'commitGroups' | 'pullRequest' | 'repository' | 'timeOutMessage' | 'truncated' | 'aliveChannels'
> & {
  metadata: Pick<CommitsRouteWithHeaderDataPayload['metadata'], 'deferredCommitsDataUrl' | 'commitHeadShaChannel'>
} & {
  aliveChannelThrottleTimeout?: number
  dataUpdatedAt?: number
}) {
  // Status updates
  useCommitsLiveUpdates(commitHeadShaChannel, aliveChannelThrottleTimeout)
  // Commit list updates
  useCommitsLiveUpdates(aliveChannels.pullRequest, aliveChannelThrottleTimeout)

  const deferredCommitsData = useLoadDeferredCommitDataPaginated(
    deferredCommitsDataUrl,
    //currently passing in 0 to start at the beginning, but if we want to paginate in the future this would be a variable
    0,
    pullRequest.commitsCount,
    dataUpdatedAt,
  )
  const {sendAnalyticsEvent} = useAnalytics()

  const loggingInfo = {commitCount: pullRequest.commitsCount, prNumber: pullRequest.number}

  const onCommitsPageViewed = useEffectEvent(() => {
    sendAnalyticsEvent(`${loggingPrefix}page_view`, 'COMMITS_PAGE_VIEWED', loggingInfo)
  })

  useEffect(() => {
    onCommitsPageViewed()
  }, [])

  const sidePanelOpen = useIsDockedPanelOpen()
  const isCompact = useIsDockedPanelCompact()

  const updatedHeader = isFeatureEnabled('updated_pull_request_header')

  return (
    <>
      <h2 className="sr-only">Commits</h2>

      <SplitPageLayout.Content as="div" width="full" padding="none" className="tmp-mt-3">
        <div
          data-testid="commits-list"
          {...hpcAttrs()}
          className={clsx(styles.layout, updatedHeader && styles.constrainedContent, updatedHeader && 'tmp-pb-3')}
          data-side-panel-open={sidePanelOpen && !isCompact}
        >
          {commitGroups.length === 0 && <CommitsBlankState timeoutMessage={timeOutMessage} />}
          {commitGroups.length > 0 && (
            <>
              {truncated && (
                <Flash variant="warning" className="tmp-mb-3">
                  This pull request is big! We&apos;re only showing the most recent 250 commits
                </Flash>
              )}
              <CommitsList
                commitGroups={commitGroups}
                deferredCommitData={deferredCommitsData}
                repository={repository}
                loggingPrefix={loggingPrefix}
                loggingPayload={loggingInfo}
              />
            </>
          )}
        </div>
      </SplitPageLayout.Content>
    </>
  )
}
